import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Container,
  Dialog,
  Grid,
  IconButton,
} from "@mui/material";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import { TableCellType } from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import SortableTable from "components/SortableTable";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useCallback, useMemo, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  ListRequestOfSkuKnowledgeLevelFilter,
  ListResponseOfSkuKnowledgeLevel,
  SkuKnowledgeLevel,
} from "services/KnowfullyAdminClient";
import { Severity, SnackbarMessageProps } from "types/common-types";
import { SkuFormProps, SkuKnowledgeLevelWithId } from "types/sku-related-types";
import SkuKnowledgeLevelForm from "./SkuKnowledgeLevelForm";

const SkuKnowledgeLevels = ({ sku, onSkuUpdated, ...rest }: SkuFormProps) => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { skuService } = useGlobalContext();
  const [deleting, setDeleting] = useState<SkuKnowledgeLevel | null>(null);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [createOpen, createOpenActions] = useBoolean(false);

  const DeleteButton = ({ value }: { value: SkuKnowledgeLevel }) => {
    return (
      <Box textAlign="center">
        <IconButton size="small" onClick={() => setDeleting(value)}>
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  };

  const handleDelete = useCallback(async () => {
    const id = deleting?.id;
    setDeleting(null);
    if (id) {
      try {
        await registerPromise(skuService.deleteSkuKnowledgeLevel(id));
        queryClient.invalidateQueries(["skuKnowledgeLevels", sku.id!]);
        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "skuKnowledgeLevelDeleted" }),
            Severity.Warning
          )
        );
      } catch (err: any) {
        console.log(err);
      }
    }
  }, [deleting?.id, queryClient, registerPromise, sku.id, skuService, t]);

  const { data, isFetching } = useStrictQuery<ListResponseOfSkuKnowledgeLevel>(
    ["skuKnowledgeLevels", sku.id!],
    () =>
      skuService.getSkuKnowledgeLevels(
        sku.id!,
        ListRequestOfSkuKnowledgeLevelFilter.fromJS({
          sortField: "createdOn",
          sortDescending: true,
        })
      )
  );

  const skuKnowledgeLevels = useMemo(
    () => (data?.data ?? []) as SkuKnowledgeLevelWithId[],
    [data?.data]
  );

  const onSkuKnowledgeLevelCreated = () => {
    createOpenActions.setFalse();
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "skuKnowledgeLevelCreated" }))
    );
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "knowledgeLevel.value",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "skuKnowledgeLevelTitle" }),
    },
    {
      accessor: "profession.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "professionLabel" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: DeleteButton,
      label: "",
      noSort: true,
    },
  ];

  return (
    <Container>
      <Box mb={3}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Box textAlign="right">
              <Button
                variant="outlined"
                type="submit"
                color="primary"
                onClick={createOpenActions.setTrue}
                data-cy="addKnowledgeLevelButton"
              >
                <T id="skuKnowledgeLevelCreateButtonLabel" />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <SortableTable
        {...{
          rows: skuKnowledgeLevels,
          loading: isFetching,
          dense: true,
          cells,
        }}
      />

      <DeleteEntityDialog
        dialogTitleKey="skuKnowledgeLevelDeleteTitle"
        contentKey="skuKnowledgeLevelDeleteQuestion"
        open={!!deleting}
        handleDelete={handleDelete}
        onCancel={() => setDeleting(null)}
      />

      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <SkuKnowledgeLevelForm
          sku={sku}
          onSkuUpdated={onSkuUpdated}
          onSkuKnowledgeLevelCreated={onSkuKnowledgeLevelCreated}
          onCancel={createOpenActions.setFalse}
          {...rest}
        />
      </Dialog>

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default withErrorBoundary(SkuKnowledgeLevels);
